import { z } from "zod";

/**
 * Bestandsvergabe je Kunde: in welcher Reihenfolge offene Aufträge (noch nicht im Lager) den verfügbaren Bestand
 * bekommen. Connect steuert die Übergabe – ein Auftrag geht erst ans Lager, wenn ihm Bestand zugeteilt ist, damit nie
 * das Lager über die Reihenfolge entscheidet.
 *
 * - fifo: wer zuerst bestellt, bekommt zuerst. Streng: ein Auftrag, der noch nicht komplett lieferbar ist, hält trotzdem
 *   seine Ware – ein späterer Auftrag (auch mit nur einer Position) überholt ihn nie.
 * - max_orders („Möglichst viele Pakete“): der Bestand wird so verteilt, dass möglichst viele Aufträge komplett
 *   versendet werden können (kleine Aufträge mit knappen Artikeln zuerst). Nicht lieferbare Aufträge halten nichts.
 *
 * Vor der Strategie gelten Vorrangklassen: manuell vorgezogen › länger als N Tage wartend › Kanal-Vorrang › Rest.
 */
export const reservationSettingsSchema = z.object({
  strategy: z.enum(["fifo", "max_orders"]).default("fifo"),
  /** Kanäle mit Vorrang, wichtigster zuerst – nicht genannte Kanäle danach */
  channelPriority: z.array(z.string().uuid()).max(50).default([]),
  /** Schutz vor Dauerwarten: Aufträge, die länger warten, rücken vor jeden Kanal-Vorrang (leer = aus) */
  agingDays: z.number().int().min(1).max(90).nullable().default(null),
  /** Aufträge in Klärung (Adresse, Zahlung, Regel …) halten ihren Bestand höchstens so viele Tage (0 = nie, leer = unbegrenzt) */
  clarificationDays: z.number().int().min(0).max(90).nullable().default(3),
});
export type ReservationSettings = z.infer<typeof reservationSettingsSchema>;
export const DEFAULT_RESERVATION: ReservationSettings = reservationSettingsSchema.parse({});

export interface ReservationOrder {
  id: string;
  channelId: string;
  placedAt: Date;
  /** manuell vorgezogen (wann) */
  boostedAt: Date | null;
  /** seit wann in Klärung – null = nicht in Klärung */
  clarificationSince: Date | null;
  /** Bedarf je Artikel (Sets bereits in Komponenten aufgelöst) */
  need: Map<string, number>;
}

export type ReservationClass = "boosted" | "aging" | "channel" | "normal" | "released";

export interface Allocation {
  /** komplett lieferbar mit zugeteiltem Bestand */
  covered: boolean;
  /** Platz in der Vergabe-Reihenfolge (1 = zuerst) – null, wenn der Auftrag gerade nichts hält (Klärung abgelaufen) */
  position: number | null;
  klass: ReservationClass;
  /** frei für diesen Auftrag je Artikel (nach allen Aufträgen vor ihm) */
  free: Map<string, number>;
  shortages: { productId: string; needed: number; free: number }[];
}

const DAY = 86_400_000;

/** hält der Auftrag gerade Bestand? Klärfälle nur bis zur eingestellten Frist */
export function reservesStock(o: Pick<ReservationOrder, "clarificationSince" | "boostedAt">, s: ReservationSettings, now: Date): boolean {
  if (!o.clarificationSince || o.boostedAt) return true;
  if (s.clarificationDays == null) return true;
  return now.getTime() - o.clarificationSince.getTime() < s.clarificationDays * DAY;
}

function classOf(o: ReservationOrder, s: ReservationSettings, now: Date): { klass: ReservationClass; rank: number } {
  if (o.boostedAt) return { klass: "boosted", rank: 0 };
  if (s.agingDays != null && now.getTime() - o.placedAt.getTime() >= s.agingDays * DAY) return { klass: "aging", rank: 1 };
  const i = s.channelPriority.indexOf(o.channelId);
  if (i >= 0) return { klass: "channel", rank: 2 + i };
  return { klass: "normal", rank: 2 + s.channelPriority.length };
}

const byAge = (a: ReservationOrder, b: ReservationOrder) => a.placedAt.getTime() - b.placedAt.getTime() || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * Teilt `available` (je Artikel) den Aufträgen zu. Deterministisch: gleiche Eingabe → gleiche Zuteilung, damit die
 * Prüfung am einzelnen Auftrag und der Lauf über alle Aufträge dasselbe Ergebnis liefern.
 */
export function allocateStock(orders: ReservationOrder[], available: Map<string, number>, s: ReservationSettings, now: Date): Map<string, Allocation> {
  const out = new Map<string, Allocation>();
  const left = new Map(available);
  const freeFor = (o: ReservationOrder) => new Map([...o.need.keys()].map((p) => [p, Math.max(0, left.get(p) ?? 0)]));
  const shortagesOf = (o: ReservationOrder, free: Map<string, number>) =>
    [...o.need].filter(([p, n]) => n > (free.get(p) ?? 0)).map(([p, n]) => ({ productId: p, needed: n, free: free.get(p) ?? 0 }));
  const take = (o: ReservationOrder) => {
    for (const [p, n] of o.need) left.set(p, Math.max(0, (left.get(p) ?? 0) - n));
  };

  const active: ReservationOrder[] = [];
  for (const o of orders) {
    if (reservesStock(o, s, now)) active.push(o);
    else out.set(o.id, { covered: false, position: null, klass: "released", free: new Map(), shortages: [] });
  }
  // Vorrangklassen nacheinander (manuell vorgezogen zuerst nach Zeitpunkt des Vorziehens)
  const classes = new Map<number, { klass: ReservationClass; orders: ReservationOrder[] }>();
  for (const o of active) {
    const c = classOf(o, s, now);
    const g = classes.get(c.rank) ?? { klass: c.klass, orders: [] };
    g.orders.push(o);
    classes.set(c.rank, g);
  }
  let position = 0;
  for (const rank of [...classes.keys()].sort((a, b) => a - b)) {
    const g = classes.get(rank)!;
    if (g.klass === "boosted") g.orders.sort((a, b) => a.boostedAt!.getTime() - b.boostedAt!.getTime() || byAge(a, b));
    else g.orders.sort(byAge);

    if (s.strategy === "fifo" || g.klass === "boosted") {
      // streng: jeder Auftrag hält seine Ware, auch wenn er noch nicht komplett ist
      for (const o of g.orders) {
        const free = freeFor(o);
        const shortages = shortagesOf(o, free);
        out.set(o.id, { covered: shortages.length === 0, position: ++position, klass: g.klass, free, shortages });
        take(o);
      }
      continue;
    }

    // Möglichst viele Pakete: Aufträge nach „Anteil am knappen Bestand“ (Summe Bedarf/verfügbar je Artikel) – wer wenig
    // vom Knappen braucht, zuerst; bei Gleichstand der ältere. Nicht komplett lieferbare halten nichts.
    const cost = (o: ReservationOrder) => {
      let c = 0;
      for (const [p, n] of o.need) {
        const a = left.get(p) ?? 0;
        if (n > a) return Number.POSITIVE_INFINITY;
        c += n / a;
      }
      return c;
    };
    const ranked = g.orders.map((o) => ({ o, c: cost(o) })).sort((a, b) => a.c - b.c || byAge(a.o, b.o));
    const waiting: ReservationOrder[] = [];
    for (const { o } of ranked) {
      const free = freeFor(o);
      const shortages = shortagesOf(o, free);
      if (shortages.length) {
        waiting.push(o);
        continue;
      }
      out.set(o.id, { covered: true, position: ++position, klass: g.klass, free, shortages });
      take(o);
    }
    // wartende Aufträge stehen danach in Eingangsreihenfolge (Platz = Reihenfolge für den nächsten Zulauf)
    for (const o of waiting.sort(byAge)) {
      const free = freeFor(o);
      out.set(o.id, { covered: false, position: ++position, klass: g.klass, free, shortages: shortagesOf(o, free) });
    }
  }
  return out;
}

/** lesbare Kurzbeschreibung für Verlauf */
export function reservationText(s: ReservationSettings, channelName: (id: string) => string = (id) => id): string {
  const parts = [s.strategy === "fifo" ? "Reihenfolge: wer zuerst bestellt, bekommt zuerst (streng)" : "Reihenfolge: möglichst viele Pakete"];
  if (s.channelPriority.length) parts.push(`Vorrang: ${s.channelPriority.map(channelName).join(" › ")}`);
  if (s.agingDays != null) parts.push(`nach ${s.agingDays} Tagen Wartezeit vorrangig`);
  parts.push(s.clarificationDays == null ? "Klärfälle halten Bestand unbegrenzt" : s.clarificationDays === 0 ? "Klärfälle halten keinen Bestand" : `Klärfälle halten Bestand ${s.clarificationDays} Tage`);
  return parts.join(" · ");
}
